const DATE_FORMAT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/;
const ID_CHARS =
  "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ_-";

export type WithErrors<T> = Partial<
  { [K in keyof T]: string[] | string } & { non_field_errors?: string[] }
>;
export type PropType<TObj, TProp extends keyof TObj> = TObj[TProp];

export class RequestError<T> extends Error {
  response: Response;
  data: WithErrors<T> | null | string;
  code: number;
  constructor(response: Response, data: WithErrors<T> | null | string) {
    super(response.statusText);
    Object.setPrototypeOf(this, RequestError.prototype);
    this.response = response;
    this.data = data;
    this.code = response.status;
  }
}

enum HTTPMethod {
  GET = "GET",
  POST = "POST",
  PUT = "PUT",
  PATCH = "PATCH",
  DELETE = "DELETE",
}

function reviver(key: string, value: unknown) {
  if (typeof value === "string" && DATE_FORMAT.test(value))
    return new Date(value);
  return value;
}

export function generateID(length: number = 8): string {
  let id = "";
  for (let i = 0; i < length; i++) {
    id +=
      ID_CHARS[Math.floor(Math.random() * ID_CHARS.length) % ID_CHARS.length];
  }
  return id;
}

export function parseJSON<T>(json: string): T | null {
  try {
    return JSON.parse(json, reviver);
  } catch (e) {
    console.error(e);
    return null;
  }
}

export function getNumberValue(id: string): number {
  const el = document.getElementById(id);
  if (!el) return NaN;
  if (el instanceof HTMLInputElement) return parseInt(el.value);
  else return parseInt(el.textContent ?? "", 10);
}

export function setNumberValue(id: string, value: number): void {
  const el = document.getElementById(id);
  if (!el) return;
  if (el instanceof HTMLInputElement) el.value = "" + value;
  else el.textContent = "" + value;
}

export function getStringValue(id: string): string {
  const el = document.getElementById(id);
  if (!el) return "";
  if (el instanceof HTMLInputElement) return el.value;
  else if (el instanceof HTMLSelectElement)
    return el.options[el.selectedIndex].value;
  else return el.textContent ?? "";
}

function storageSaveJSON<T>(storage: Storage, key: string, data: T): void {
  storage.setItem(key, JSON.stringify(data));
}

function storageRetrieveJSON<T>(
  storage: Storage,
  key: string,
  defaultValue?: T,
): T | null {
  const str = storage.getItem(key);
  if (str === null && defaultValue !== undefined) return defaultValue;
  if (str === null) return null;
  const value = parseJSON<T>(str);
  if (!value) return null;
  return value;
}

export function localStoreJSON<T>(key: string, data: T): void {
  storageSaveJSON(localStorage, key, data);
}

export function localRetrieveJSON<T>(key: string): T | null;
export function localRetrieveJSON<T>(key: string, defaultValue: T): T;
export function localRetrieveJSON<T>(key: string, defaultValue?: T): T | null {
  return storageRetrieveJSON(localStorage, key, defaultValue);
}

export function sessionStoreJSON<T>(key: string, data: T): void {
  storageSaveJSON(sessionStorage, key, data);
}

export function sessionRetrieveJSON<T>(key: string): T | null;
export function sessionRetrieveJSON<T>(key: string, defaultValue: T): T;
export function sessionRetrieveJSON<T>(
  key: string,
  defaultValue?: T,
): T | null {
  return storageRetrieveJSON(sessionStorage, key, defaultValue);
}

export function generateAuthenticationHeaders(
  token: string,
): Record<string, string> | undefined {
  if (token) {
    return { Authorization: `Token ${token}` };
  }
  return undefined;
}

export async function requestJSON<T>(
  url: string,
  method: HTTPMethod = HTTPMethod.GET,
  body?: BodyInit,
  headers?: Record<string, string>,
): Promise<T | null> {
  const defaultHeaders = { Accept: "application/json" };
  try {
    if (headers) {
      headers = { ...defaultHeaders, ...headers };
    } else {
      headers = defaultHeaders;
    }
    let init: RequestInit = {
      method: method.valueOf(),
      headers,
      cache: "no-cache",
    };
    if (body !== undefined && method !== HTTPMethod.GET) {
      init.body = body;
    }
    const response = await fetch(url, init);
    const responseText = await response.text();
    let data = null;
    try {
      if (response.status === 204 && responseText.length === 0) {
        return null;
      } else if (responseText.length === 0) {
        throw new RequestError(response, null);
      }
      if (response.ok) {
        data = parseJSON<T>(responseText);
      } else {
        const dataErrors = parseJSON<WithErrors<T>>(responseText);
        throw new RequestError(response, dataErrors);
      }
    } catch (e) {
      if (e instanceof RequestError) {
        throw e;
      } else {
        throw new RequestError(response, responseText);
      }
    }
    return data;
  } catch (e) {
    if (e instanceof RequestError) {
      throw e;
    }
    return e as T | null;
  }
}

export type ResultsPage<T> = {
  results: T[];
  next: string | null;
  previous: string | null;
  count: number;
};

/**
 * Fetches all pages of paginated data from an API endpoint
 * @param url - The initial API endpoint
 * @param token - Optional authentication token
 * @param headers - Optional additional headers
 * @returns Promise resolving to combined results from all pages
 */
export async function getJSONAllPages<T>(
  url: string,
  token: string | null,
  headers?: Record<string, string>,
): Promise<T[]> {
  try {
    // Fetch the first page with the appropriate method based on token
    const firstPageData = token
      ? await getAuthJSON<ResultsPage<T>>(url, token, headers)
      : await getJSON<ResultsPage<T>>(url, headers);

    if (!firstPageData) {
      throw new Error("Failed to fetch initial data");
    }

    // If there's no next page, return the data as is
    if (!firstPageData.next) {
      return firstPageData.results;
    }

    // Initialize the result with the first page data
    const result = firstPageData.results;

    // Prepare to fetch all pages
    let currentPage: ResultsPage<T> | null = firstPageData;

    // Keep fetching until there are no more pages
    while (currentPage.next) {
      // Rewrite the next URL's origin to match the current window origin,
      // in case DRF returns a different host/scheme (e.g. http vs https)
      let nextUrl: string = currentPage.next;
      try {
        const parsed = new URL(nextUrl);
        const current = new URL(window.location.origin);
        if (parsed.origin !== current.origin) {
          parsed.protocol = current.protocol;
          parsed.host = current.host;
          nextUrl = parsed.toString();
        }
      } catch (_) {
        // If parsing fails, use the URL as-is
      }
      // Fetch the next page
      const url: string = nextUrl;
      currentPage = token
        ? await getAuthJSON(url, token, headers)
        : await getJSON(url, headers);

      if (!currentPage) {
        console.warn(`Failed to fetch page at ${url}, stopping pagination`);
        break;
      }

      // Add the results from this page to our combined results
      result.push(...currentPage.results);
    }

    return result;
  } catch (error) {
    console.error("Error fetching all pages:", error);
    throw error;
  }
}

export async function getJSON<T>(
  url: string,
  headers?: Record<string, string>,
): Promise<T | null> {
  return requestJSON(url, HTTPMethod.GET, undefined, headers);
}

export function getAuthJSON<T>(
  url: string,
  token: string,
  headers?: Record<string, string>,
): Promise<T | null> {
  const newHeaders = {
    ...generateAuthenticationHeaders(token),
    ...headers,
  };
  return getJSON(url, newHeaders);
}

export async function postJSON<T, U>(
  url: string,
  data?: T,
  headers?: Record<string, string>,
): Promise<U | null> {
  headers = {
    ...headers,
    "Content-Type": "application/json",
  };
  return requestJSON(url, HTTPMethod.POST, JSON.stringify(data), headers);
}

export async function putJSON<T, U>(
  url: string,
  data: T,
  headers?: Record<string, string>,
): Promise<U | null> {
  headers = {
    "Content-Type": "application/json",
    ...headers,
  };
  return requestJSON(url, HTTPMethod.PUT, JSON.stringify(data), headers);
}

export function postAuthJSON<T, U>(
  url: string,
  token: string,
  data?: T,
  headers?: Record<string, string>,
): Promise<U | null> {
  const newHeaders = {
    ...generateAuthenticationHeaders(token),
    ...headers,
  };
  return postJSON(url, data, newHeaders);
}

export function putAuthJSON<T, U>(
  url: string,
  token: string,
  data?: T,
  headers?: Record<string, string>,
): Promise<U | null> {
  const newHeaders = {
    ...generateAuthenticationHeaders(token),
    ...headers,
  };
  return putJSON(url, data, newHeaders);
}

export async function patchJSON<T, U>(
  url: string,
  data: T,
  headers?: Record<string, string>,
): Promise<U | null> {
  headers = {
    ...headers,
    "Content-Type": "application/json",
  };
  return requestJSON(url, HTTPMethod.PATCH, JSON.stringify(data), headers);
}

export function patchAuthJSON<T, U>(
  url: string,
  token: string,
  data: T,
  headers?: Record<string, string>,
): Promise<U | null> {
  const newHeaders = {
    ...generateAuthenticationHeaders(token),
    ...headers,
  };
  return patchJSON(url, data, newHeaders);
}

export async function deleteResource(
  url: string,
  headers?: Record<string, string>,
): Promise<null> {
  return requestJSON(url, HTTPMethod.DELETE, undefined, headers);
}

export function deleteAuthResource<U>(
  url: string,
  token: string,
  headers?: Record<string, string>,
): Promise<U | null> {
  const newHeaders = {
    ...generateAuthenticationHeaders(token),
    ...headers,
  };
  return deleteResource(url, newHeaders);
}

export async function sendFile<T>(
  url: string,
  file: string | Blob,
  formName: string,
  headers?: Record<string, string>,
  method: HTTPMethod = HTTPMethod.PATCH,
): Promise<T | null> {
  try {
    let body: FormData = new FormData();
    body.append(formName, file);
    let init: RequestInit = {
      method: method.valueOf(),
      cache: "no-cache",
      body,
    };
    if (headers) {
      if (headers["Content-Type"]) {
        delete headers["Content-Type"]; //must not include content-type here: https://stackoverflow.com/a/49510941/4802259
        console.warn(
          "Do not set the Content-Type header for file upload requests. headers['Content-Type'] was removed before sending the request.",
        );
      }
      init.headers = headers;
    }

    const response = await fetch(url, init);
    let data = null;
    try {
      data = parseJSON<T>(await response.text());
    } catch (e) {
      data = await response.text();
      throw new RequestError(response, data);
    }
    if (!response.ok && data != null) {
      throw new RequestError(response, data);
    }
    return data;
  } catch (e) {
    if (e instanceof RequestError) {
      throw e;
    }
    console.error(e);
    return null;
  }
}

export async function sendAuthFile<T>(
  url: string,
  file: string | Blob,
  formName: string,
  token: string,
  headers?: Record<string, string>,
  method: HTTPMethod = HTTPMethod.PATCH,
): Promise<T | null> {
  const newHeaders = {
    ...generateAuthenticationHeaders(token),
    ...headers,
  };
  return sendFile(url, file, formName, newHeaders, method);
}

export function* chunked<T>(arr: Array<T>, size: number) {
  for (let i = 0; i < arr.length; i += size) {
    yield arr.slice(i, i + size);
  }
}

export class Semaphore<T> {
  max: number;
  count: number;
  queue: Array<() => void>;
  constructor(max: number = 5) {
    this.max = max;
    this.count = 0;
    this.queue = [];
  }

  acquire() {
    return new Promise<void>((resolve) => {
      if (this.count < this.max) {
        this.count++;
        resolve();
      } else {
        this.queue.push(resolve);
      }
    });
  }

  release() {
    if (this.queue.length > 0) {
      const resolve = this.queue.shift();
      if (resolve === undefined) {
        this.count--;
      } else {
        resolve();
      }
    } else if (this.count > 0) {
      this.count--;
    }
  }

  async use(callback: () => Promise<T>): Promise<T> {
    await this.acquire();
    try {
      return await callback();
    } finally {
      this.release();
    }
  }
}
